"use client";

import { useMemo, useCallback } from "react";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { WALLET_STATUS } from "@/constants/wallet";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useApi } from "@/hooks/core/useApi";
import { isWalletActionError } from "@/hooks/core/useWalletActionGuard";
import { useSolanaWallet } from "@/hooks/solana/useSolanaWallet";
import { useHubUser } from "@/hooks/user/useHubUser";
import { prepareZeroOneSession, authorizeZeroOneSession } from "@/utils/airdrop-farming/zeroone-session-auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

/**
 * Session status for 01.xyz connection flow.
 * Compatible with ConnectAccountStep expectations.
 */
export type ZeroOneSessionStatus =
  | "checking-wallet"
  | "needs-wallet"
  | "needs-approval"
  | "checking-activation"
  | "ready";

/**
 * Hook for managing 01.xyz connection flow via Solana wallet signing.
 *
 * Flow:
 * 1. User connects their Solana wallet (Phantom/Solflare via wallet adapter)
 * 2. Backend generates session keypair + message to sign (prepare phase)
 * 3. User signs message with Solana wallet (Ed25519)
 * 4. Signature sent to backend which creates NordUser session and stores credentials
 *
 * Note: Unlike EVM exchanges, this uses Ed25519 signing (Solana) instead of EIP-712.
 * The Solana wallet connection is managed externally (wallet adapter provider).
 */
export function useZeroOneConnection() {
  const { auth, hubUser, walletStatus } = useHubUser();
  const apiHelper = useApi();
  const { authenticated } = useAuthenticationProvider();
  const { linkedSolanaAddress, activeInjectedWalletAddress, isSolanaConnected } = useSolanaWallet();
  const queryClient = useQueryClient();

  const zerooneAccounts = useMemo(
    () => hubUser?.exchangeAccounts?.filter((a) => a.exchangeId === SUPPORTED_EXCHANGES.ZEROONE) ?? [],
    [hubUser?.exchangeAccounts],
  );

  const hasZeroOneAccount = zerooneAccounts.length > 0;

  const {
    mutateAsync: connectZeroOne,
    isPending: isConnecting,
    error: connectionError,
    reset: resetConnectionError,
  } = useMutation({
    mutationFn: async (params: {
      solanaAddress: string;
      signMessage: (message: Uint8Array) => Promise<Uint8Array>;
    }) => {
      // Phase 1: Prepare — get message to sign from backend
      const prepareResult = await prepareZeroOneSession(apiHelper, auth, params.solanaAddress);

      // Phase 2: Sign — user signs message with Solana wallet
      const messageBytes = new TextEncoder().encode(prepareResult.messageToSign);
      const signatureBytes = await params.signMessage(messageBytes);
      const signature = Buffer.from(signatureBytes).toString("base64");

      // Phase 3: Authorize — send signature to backend
      await authorizeZeroOneSession(apiHelper, auth, {
        prepareId: prepareResult.prepareId,
        signature,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      enqueueSnackbar("01.xyz account connected successfully", { variant: "success" });
    },
    onError: (error) => {
      if (isWalletActionError(error)) return;
      const msg = error instanceof Error ? error.message : "Failed to connect 01.xyz";
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const approveAgent = useCallback(
    (params?: { solanaAddress?: string; signMessage?: (message: Uint8Array) => Promise<Uint8Array> }) => {
      if (!params?.solanaAddress || !params?.signMessage) {
        return Promise.reject(new Error("Solana wallet not connected"));
      }
      return connectZeroOne({
        solanaAddress: params.solanaAddress,
        signMessage: params.signMessage,
      });
    },
    [connectZeroOne],
  );

  const sessionStatus: ZeroOneSessionStatus =
    walletStatus === WALLET_STATUS.INITIALIZING || walletStatus === WALLET_STATUS.CONNECTING
      ? "checking-wallet"
      : !authenticated
        ? "needs-wallet"
        : isConnecting
          ? "checking-activation"
          : hasZeroOneAccount
            ? "ready"
            : "needs-approval";

  return {
    // Wallet state — 01.xyz actions use the linked Solana wallet, not the main EVM wallet.
    connectedAddress:
      linkedSolanaAddress ?? activeInjectedWalletAddress ?? zerooneAccounts[0]?.publicApiKey ?? undefined,
    isWalletConnected: isSolanaConnected,

    // Account state
    zerooneAccounts,
    hasZeroOneAccount,

    // Actions
    connectZeroOne,
    isConnecting,
    connectionError,
    resetConnectionError,

    // Session status
    sessionStatus,

    // ── Shape compatible with useHyperliquidConnection for useLaunchBotForm ──
    agentData: {
      accountId: zerooneAccounts[0]?.id ?? 0,
      agentAddress: zerooneAccounts[0]?.publicApiKey ?? "",
      agentName: "",
    },
    hasAgentData: hasZeroOneAccount,
    isLoadingAgent: false,
    isCheckingEligibility: false,
    isNotEligibleError: false,
    eligibilityData: { eligible: true } as const,
    isEligible: true,
    approveAgent,
    isApproving: isConnecting,
    approvalError: connectionError,
    resetApprovalError: resetConnectionError,
    // Not applicable for 01.xyz
    updateHyperliquidAgentStatus: async () => {},
    isUpdatingAgentStatus: false,
    isLoading: isConnecting,
    walletStatus,

    authenticated,
  };
}
